import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchOverviewData } from '../store/analyticsSlice';
import { OverviewKpiGrid } from './OverviewKpiGrid';
import { OverviewCharts } from './OverviewCharts';
import { OverviewDistributions } from './OverviewDistributions';
import { Skeleton } from '@/components/ui/skeleton';
import { calculateAnalyticsDateRange } from '@/lib/dateUtils.ts';

/**
 * Container for the global workspace analytics overview.
 * Orchestrates the data fetching based on workspace and time period.
 */
export const OverviewTab: React.FC = () => {
  const dispatchAction = useAppDispatch();
  const currentWorkspaceIdentifier = useAppSelector(
    (state) => state.workspace.currentWorkspace?.id,
  );
  const { loading, stats, period } = useAppSelector((state) => state.analytics.overview);

  useEffect(() => {
    if (currentWorkspaceIdentifier) {
      const { start: calculatedStartDateIso, end: calculatedEndDateIso } =
        calculateAnalyticsDateRange(period);

      dispatchAction(
        fetchOverviewData({
          workspaceId: currentWorkspaceIdentifier,
          from: calculatedStartDateIso,
          to: calculatedEndDateIso,
        }),
      );
    }
  }, [currentWorkspaceIdentifier, dispatchAction, period]);

  if (loading && !stats) {
    return (
      <div className="flex flex-col gap-10 animate-pulse">
        <Skeleton className="h-[200px] w-full rounded-[20px]" />
        <Skeleton className="h-[400px] w-full rounded-[20px]" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-[440px] w-full rounded-[20px]" />
          <Skeleton className="h-[440px] w-full rounded-[20px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 pb-10">
      <OverviewKpiGrid />
      <OverviewCharts />
      <OverviewDistributions />
    </div>
  );
};
